import type { EventsStoryblok } from '~/types/storyblok'
import type { ISbStoryData } from 'storyblok'

function formattedEvent(date: string) {
  const options = {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric',
  } as Intl.DateTimeFormatOptions
  return dateFormat(date, options)
}

function formattedEventDay(date: string) {
  const options = {
    day: '2-digit',
  } as Intl.DateTimeFormatOptions
  return dateFormat(date, options)
}

function formattedEventMonth(date: string, month: 'long' | 'short' = 'short') {
  const options = {
    month,
  } as Intl.DateTimeFormatOptions
  return dateFormat(date, options)
}

export default function useEventContent({
  blok,
  big,
}: {
  blok: Ref<EventsStoryblok> | Ref<ISbStoryData<EventsStoryblok>>
  big?: Ref<boolean>
}) {
  // Correctly infer the type of blok.value to get the event content cleanly
  function isEventContent(
    event: EventsStoryblok | ISbStoryData<EventsStoryblok>,
  ): event is EventsStoryblok {
    return (event as EventsStoryblok)?.component !== undefined
  }
  const eventContent = computed(() =>
    isEventContent(blok.value) ? blok.value : blok.value.content,
  )

  const isRegularEvent = computed(() => eventContent.value?.regular_event)
  const eventIsSameDay = computed(() => {
    const fmt = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    } as Intl.DateTimeFormatOptions
    return dateFormat(eventStart.value, fmt) === dateFormat(eventEnd.value, fmt)
  })
  const eventStart = computed(() => eventContent.value?.date_start || '')
  const eventStartFormatted = computed(() => formattedEvent(eventStart.value))
  const eventStartFormattedDay = computed(() =>
    formattedEventDay(eventStart.value),
  )
  const eventStartFormattedMonth = computed(() =>
    formattedEventMonth(eventStart.value, big.value ? 'long' : 'short'),
  )
  const eventEnd = computed(() => eventContent.value?.date_end || '')
  const eventEndFormatted = computed(() => formattedEvent(eventEnd.value))
  const eventEndFormattedDay = computed(() => formattedEventDay(eventEnd.value))
  const eventEndFormattedMonth = computed(() =>
    formattedEventMonth(eventEnd.value, big.value ? 'long' : 'short'),
  )
  const eventIsAllDay = computed(() => eventContent.value?.date_is_all_day)
  const hasEndDate = computed(
    () =>
      eventEndFormatted.value && !(eventIsSameDay.value || eventIsAllDay.value),
  )
  const singleEventClass = computed(() =>
    eventIsStarted.value ? 'start' : 'end',
  )
  const eventIsStarted = computed(() => new Date(eventStart.value) > new Date())
  const eventSingleFormatted = computed(() =>
    eventIsStarted.value ? eventStartFormatted.value : eventEndFormatted.value,
  )
  const eventSingleFormattedDay = computed(() =>
    eventIsStarted.value
      ? eventStartFormattedDay.value
      : eventEndFormattedDay.value,
  )
  const eventSingleFormattedMonth = computed(() =>
    eventIsStarted.value
      ? eventStartFormattedMonth.value
      : eventEndFormattedMonth.value,
  )
  const eventLocation = computed(() => eventContent.value?.location)
  const eventStartTimeFormatted = computed(() => timeFormat(eventStart.value))
  const eventEndTimeFormatted = computed(() => timeFormat(eventEnd.value))

  return {
    eventStart,
    eventStartFormatted,
    eventStartFormattedDay,
    eventStartFormattedMonth,
    eventEnd,
    eventEndFormatted,
    eventEndFormattedDay,
    eventEndFormattedMonth,
    eventIsAllDay,
    hasEndDate,
    singleEventClass,
    eventIsStarted,
    eventSingleFormatted,
    eventSingleFormattedDay,
    eventSingleFormattedMonth,
    isRegularEvent,
    eventIsSameDay,
    eventLocation,
    eventStartTimeFormatted,
    eventEndTimeFormatted,
  }
}
